import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    name?: string;
}

interface AuthContextType {
    user: User | null;
    isLoading: boolean;
    login: (email: string, password: string) => Promise<void>;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

// API URL aus Umgebungsvariablen oder fallback
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        // Beim ersten Laden prüfen, ob ein Benutzer im localStorage gespeichert ist
        const storedUser = localStorage.getItem('user');
        const token = localStorage.getItem('token');

        if (storedUser && token) {
            try {
                setUser(JSON.parse(storedUser));
            } catch (error) {
                console.error('Failed to parse stored user:', error);
                localStorage.removeItem('user');
                localStorage.removeItem('token');
            }
        }
        setIsLoading(false);
    }, []);

    const login = async (email: string, password: string): Promise<void> => {
        setIsLoading(true);
        try {
            // Lokaler Demo-Login als Fallback, wenn der API-Aufruf fehlschlägt
            if (email === 'demo@example.com' && password === 'password') {
                try {
                    // Zuerst versuchen, über die API einzuloggen
                    const response = await fetch(`${API_URL}/api/login`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ email, password }),
                    });

                    if (response.ok) {
                        const data = await response.json();
                        localStorage.setItem('user', JSON.stringify(data.user));
                        localStorage.setItem('token', data.token);
                        setUser(data.user);
                        return;
                    }
                } catch (error) {
                    console.warn('API nicht erreichbar, verwende lokalen Demo-Login');
                }

                // Fallback: Lokaler Demo-Login
                const demoUser = {
                    id: 'demo-id',
                    first_name: 'Demo',
                    last_name: 'User',
                    email,
                    name: 'Demo User'
                };
                localStorage.setItem('user', JSON.stringify(demoUser));
                localStorage.setItem('token', 'demo-token');
                setUser(demoUser);
                return;
            }

            // API-Aufruf für alle anderen Logins
            const response = await fetch(`${API_URL}/api/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Ungültige Anmeldedaten');
            }

            const data = await response.json();

            // Benutzer und Token im localStorage speichern
            localStorage.setItem('user', JSON.stringify(data.user));
            localStorage.setItem('token', data.token);

            setUser(data.user);
        } catch (error: any) {
            console.error('Login failed:', error);
            throw new Error(error.message || 'Ungültige Anmeldedaten');
        } finally {
            setIsLoading(false);
        }
    };

    const logout = () => {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, isLoading, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};
